<template>
  <div class="popup z-index-10-fg confirmation small-popup add-interaction-popup" ref="popupWindow" @dragenter="$refs.fileUpload.dragging = true">
    <div class="popup-content" ref="popupContent">
      <div class="popup-header">
        <h2>Complete Lead</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
        <div class="paperclip-icon" @click="$refs.fileUpload.$refs.fileUploadInput.click()">
          <img id="noHover" src="../assets/paperclip.svg" alt="" />
          <img id="hover" src="../assets/paperclip-hover.svg" alt="" />
        </div>
      </div>
      <div :class="{ 'popup-body': true }">
        <Dropdown :options="completionTypes" label="Type" v-model="type" class="input-item medium-item" />

        <div class="primary-input input-item large-item">
          <textarea type="text" v-model="note" rows="4" style="resize: none"></textarea>
          <label>Note</label>
        </div>
        <div :class="['primary-input', 'input-item', 'medium-item', showErrors && !validInvNum ? 'invalid' : '']" v-if="type == 'Confirmed Order'">
          <input type="text" v-model="invNum" />
          <label>Invoice Number</label>
        </div>
        <div :class="['primary-input', 'input-item', 'medium-item', showErrors && !validAmount ? 'invalid' : '']" v-if="type == 'Confirmed Order'">
          <input type="text" v-model="amount" @blur="formatAmount()" />
          <label>Amount</label>
        </div>

        <Checkbox label="Delivery" v-model="hasDelivery" class="input-item large-item" />

        <AddressSearch
          :options="lookupResults"
          label="Site Address"
          v-model="deliveryAddress"
          class="input-item large-item"
          v-if="hasDelivery"
          @autofillAddress="(o) => (deliveryAddress = o.formattedAddress)"
          @clearLookup="lookupResults.length = 0" />

        <div class="primary-input input-item medium-item" v-if="hasDelivery">
          <input type="date" v-model="deliveryDate" />
          <label>Delivery Date</label>
        </div>

        <div :class="['primary-input', 'input-item', 'medium-item']" v-if="hasDelivery">
          <input type="text" v-model="gateCode" />
          <label>Gate Code</label>
        </div>

        <div :class="['primary-input', 'input-item', 'large-item']" v-if="hasDelivery">
          <textarea type="text" v-model="deliveryNotes" rows="4" style="resize: none"></textarea>
          <label>Delivery Notes</label>
        </div>

        <div class="file-list" v-if="fileList.length">
          <div v-for="file in fileList">
            <img src="../assets/paperclip-thumbnail.svg" />
            <span>{{ file }}</span>
          </div>
        </div>

        <FileUpload :leadid="leadid" :isPanel="true" ref="fileUpload" @updateFileList="(e) => (fileList = e)" />
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">close</button>
        <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="saveInteraction()">Complete<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import Dropdown from './inputs/Dropdown.vue';
import useAlerts from '@/composables/useAlerts';
import useLeadsQuery from '@/composables/useLeadsQuery';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import useInteractionMutate from '@/composables/useInteractionMutate';
import useLeadMutate from '@/composables/useLeadMutate';
import { computed } from 'vue';
import useCurrentUser from '@/composables/useCurrentUser';
import FileUpload from './FileUpload.vue';
import Checkbox from './inputs/Checkbox.vue';
import useAddressLookup from '@/composables/useAddressLookup';
import AddressSearch from './inputs/AddressSearch.vue';

export default {
  name: 'Reminder',
  emits: ['closePopup'],
  components: {
    Dropdown,
    FileUpload,
    Checkbox,
    AddressSearch,
  },
  props: {
    leadid: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    const { addInteraction } = useInteractionMutate();
    const { updateLead } = useLeadMutate();
    const { user } = useCurrentUser();
    const { addressLookup, lookupResults } = useAddressLookup();

    const completionTypes = computed(() => {
      return usePicklistItemsQuery.pickLists.completionTypes.items.map((item) => item.NAME);
    });

    return {
      addAlert,
      leads: useLeadsQuery.leadsIncludingSingleLead,
      completionTypes,
      addInteraction,
      updateLead,
      user,
      addressLookup,
      lookupResults,
    };
  },
  data() {
    return {
      type: 'Confirmed Order',
      isSaving: false,
      note: '',
      invNum: '',
      amount: '$',
      showErrors: false,
      fileList: [],
      hasDelivery: false,
      deliveryAddress: '',
      deliveryDate: '',
      gateCode: '',
      deliveryNotes: '',
    };
  },
  computed: {
    validInvNum() {
      if (!this.invNum) return false;
      const stripped = String(this.invNum).replace(/[^1-9]/g, '');
      return stripped.length > 0 ? true : false;
    },
    validAmount() {
      if (!this.amount) return false;
      const stripped = String(this.amount).replace(/[^1-9]/g, '');
      return stripped.length > 0 ? true : false;
    },
  },
  methods: {
    async saveInteraction() {
      if (!this.verifyFields()) return;
      this.isSaving = true;

      if (this.type != 'Confirmed Order') {
        this.invNum = null;
        this.amount = null;
      }

      const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
      if (!lead) {
        this.addAlert('Error saving interaction', 'high', 4);
        this.$emit('closePopup');
        return;
      }

      const newInteraction = {
        LEADID: this.leadid,
        NOTE: this.note,
        TYPE: this.type,
        DATE: Date.now().toString(),
        SALESPERSONID: this.user.userid,
      };

      const updatedLead = {
        COMPLETE: true,
        INVNUM: this.invNum,
        AMOUNT: this.amount?.replace(/[^0-9.]/g, '') || null,
        COMPLETEDATE: Date.now().toString(),
      };

      if (this.hasDelivery) {
        updatedLead.HASDELIVERY = true;
        updatedLead.DELIVERYADDRESS = this.deliveryAddress;
        updatedLead.DELIVERYDATE = new Date(this.deliveryDate).getTime().toString();
        updatedLead.GATECODE = this.gateCode;
        updatedLead.DELIVERYNOTES = this.deliveryNotes;
      } else {
        updatedLead.HASDELIVERY = false;
        updatedLead.DELIVERYADDRESS = null;
        updatedLead.DELIVERYDATE = null;
        updatedLead.GATECODE = null;
        updatedLead.DELIVERYNOTES = null;
      }

      await this.updateLead(this.leadid, updatedLead);
      const newInteractionResponse = await this.addInteraction(newInteraction);

      newInteraction.INTERACTIONID = newInteractionResponse.addInteraction;
      lead.interactions.push(newInteraction);

      lead.COMPLETE = true;
      lead.COMPLETEDATE = Date.now();
      lead.INVOICENUM = this.invNum;
      lead.AMOUNT = this.Amount;

      if (this.hasDelivery) {
        lead.HASDELIVERY = true;
        lead.DELIVERYADDRESS = this.deliveryAddress;
        lead.DELIVERYDATE = (new Date(this.deliveryDate).getTime() + 8 * 60 * 60 * 1000).toString();
        lead.GATECODE = this.gateCode;
        lead.DELIVERYNOTES = this.deliveryNotes;
      } else {
        lead.HASDELIVERY = false;
        lead.DELIVERYADDRESS = null;
        lead.DELIVERYDATE = null;
        lead.GATECODE = null;
        lead.DELIVERYNOTES = null;
      }

      await this.$refs.fileUpload.uploadFiles(this.leadid, newInteraction.INTERACTIONID);

      this.isSaving = false;
      this.addAlert('Completed Lead', 'low', 3);
      this.$emit('closePopup');
    },
    formatAmount() {
      let newAmount = this.amount.replace(/[^0-9.]/g, '');
      if (newAmount.length == 0) {
        this.amount = '$';
        return;
      }
      newAmount = newAmount.replace(/\.(?=.*\.)/g, '');
      if (newAmount.includes('.') && newAmount.split('.')[1].length == 0) newAmount += '00';
      if (newAmount.includes('.') && newAmount.split('.')[1].length == 1) newAmount += '0';
      if (newAmount.includes('.') && newAmount.split('.')[1].length > 2) {
        newAmount = newAmount.replace(/\./g, '');
        newAmount = newAmount.slice(0, -2) + '.' + newAmount.slice(-2);
      }
      if (newAmount.startsWith('.')) newAmount = '0' + newAmount;
      if (!newAmount.includes('.')) {
        newAmount += '.00';
      }
      if (newAmount == '.00') newAmount = '0.00';
      if (newAmount.match(/^0+(?!\.|$)/)) {
        newAmount = newAmount.replace(/^0+(?!\.|$)/, '');
      }

      newAmount = '$' + newAmount;

      this.amount = newAmount;
    },
    verifyFields() {
      if (this.type == 'Confirmed Order') {
        if (!this.validInvNum || !this.validAmount) {
          this.showErrors = true;
          this.addAlert('Please enter a valid Invoice Number and Amount or select a different type.', 'high', 5);
          return false;
        }
      }
      this.showErrors = false;
      return true;
    },

    getISODate(date) {
      return date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;
    },
    calculateWindowDimensions() {
      this.$nextTick(() => {
        const newHeight = this.$refs.popupContent.scrollHeight + 'px';
        this.$refs.popupWindow.style.height = newHeight;
      });
    },
    checkContactAssigned(prevType) {
      const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
      if (!lead.company && !lead.contact && this.type == 'Confirmed Order') {
        this.addAlert('Please assign a contact before completing a confirmed order', 'high', 4);
        this.type = prevType || 'No Response';
        return;
      }
    },
  },
  watch: {
    deliveryAddress() {
      this.addressLookup(this.deliveryAddress);
    },
    type(newType, prevType) {
      this.checkContactAssigned(prevType);
      if (this.showErrors) this.verifyFields();
    },
  },
  mounted() {
    const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
    if (lead.HASDELIVERY) this.hasDelivery = true;
    if (lead.DELIVERYADDRESS) this.deliveryAddress = lead.DELIVERYADDRESS;
    if (lead.DELIVERYDATE) this.deliveryDate = this.getISODate(new Date(parseInt(lead.DELIVERYDATE)));
    if (lead.GATECODE) this.gateCode = lead.GATECODE;
    if (lead.DELIVERYNOTES) this.deliveryNotes = lead.DELIVERYNOTES;

    this.checkContactAssigned();

    this.calculateWindowDimensions();
  },
  updated() {
    this.$refs.popupWindow.style.transition = 'height 0.5s ease';
    this.calculateWindowDimensions();
  },
};
</script>

<style lang="scss" scoped>
.add-interaction-popup {
  width: 30rem;
  max-height: 90vh;

  overflow-y: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;

    .complete-lead-section {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1.5rem 1rem;
    }

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 10%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }

      &.medium-item {
        min-width: 5rem;
        flex: 1 0 25%;
      }
    }
  }
}
</style>
